import { EmailEntity } from '@core/entities';
import { ElementRef, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { FormService, Logger } from '@core/services';
import { untilDestroyed } from '@ngneat/until-destroy';
import { GetTeamId, GetUserId, isMobileView, scrollToTop, sortData } from '@core/utils';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { ConfirmationDialogType, DeleteConfirmationOptions } from '@core/enums';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UseEmail } from '@core/usecases';
import { instanceToPlain } from 'class-transformer';
import { DropDownItem } from '@core/types/Dropdown.type';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { PERMISSIONS, PermissionService } from '@auth';
import { ButtonComponent } from '@shared/components/ui';
import { CurrentSort } from '@core/types';

const log = new Logger('Email Template Composition');

export class EmailsComposition extends FormService {
  emails: EmailEntity[] = [];
  selectedImage: File;
  emailTypes: DropDownItem[] = [];

  sortedEmails: EmailEntity[];
  currentSort: CurrentSort = { column: '', direction: '' };

  protected readonly _route = inject(ActivatedRoute);
  private readonly _toast = inject(HotToastService);
  private readonly _translate = inject(TranslateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheet = inject(NgxBottomSheetService);
  private readonly _router = inject(Router);
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _permissionService = inject(PermissionService);

  private readonly _useEmails = new UseEmail();

  constructor() {
    super();

    this.emailTypes = [
      { value: 'course', name: this._translate.instant('course') },
      { value: 'updatedcourse', name: this._translate.instant('updatedcourse') },
      { value: 'instructor', name: this._translate.instant('instructor') },
      { value: 'survey', name: this._translate.instant('survey') },
    ];
  }

  get canCreateEmail(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_EMAIL);
  }

  onSortChange({ column, direction }: CurrentSort) {
    this.currentSort = { column, direction };
    this.sortedEmails = sortData(this.emails, column as keyof (typeof this.emails)[0], direction);
  }

  deleteEmail(email: EmailEntity) {
    if (!email.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translate.instant('Email Template'),
        },
      });

      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useEmails.deleteEmailTemplate(email.id)).then(() => {
                  this._toast.success(this._translate.instant('Delete Successful'));
                  this._fetchAllEmailTemplates();
                });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      const confirmDialog = this._dialogService.open(ConfirmationDialogComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          type: ConfirmationDialogType.DELETE,
          emphasize: email.subject,
        },
      });

      confirmDialog.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (result) => {
          if (result) {
            this.call(this._useEmails.deleteEmailTemplate(email.id)).then(() => {
              this._toast.success(this._translate.instant('Delete Successful'));
              this._fetchAllEmailTemplates();
            });
          }
        },
      });
    }
  }

  onImageSelection(image: string | File) {
    this.selectedImage = image as File;
  }

  createUpdateEmail(elRef: ElementRef, btnRef?: ButtonComponent, id?: number) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form, elRef);
      return;
    }

    // Loading data from form to mutate fields where required
    const data = this.removeNullObjects(instanceToPlain(new EmailEntity(this.removeEventFields(this.form.value))));

    // Converting data to FormData
    const body = this.jsonToFromData(this.removeEventFields(data));

    // Appending extra data to the FormData
    body.append('author', GetUserId());
    body.append('team_id', GetTeamId());

    if (this.selectedImage) {
      body.append('file', this.selectedImage);
    }

    const service$ = id ? this._useEmails.updateEmailTemplate(id, body) : this._useEmails.addEmailTemplate(body);
    const message = id ? this._translate.instant('Update Successful') : this._translate.instant('Save Successful');

    this.call(service$, btnRef)
      .then((resp) => {
        if (resp) {
          this._toast.success(message);
          this._router.navigate(['/more/emails']);
        } else {
          this._toast.error(this._translate.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error ? this.error : this._translate.instant('Something went wrong'), { duration: 10000 });
        log.error('Error while creating or updating Email Template');
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchAllEmailTemplates() {
    this.call(this._useEmails.getAllEmailTemplates())
      .then((res: EmailEntity[]) => {
        if (res) {
          this.emails = res;
          this.sortedEmails = this.emails;
          scrollToTop();
        } else {
          log.info('No Email found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Emails', err);
      });
  }

  protected getEmailById(id: number) {
    this.form.disable();
    this.isLoading = true;
    this.call(this._useEmails.getEmailTemplateById(id)).then((res: EmailEntity) => {
      if (res) {
        this.updateAndValidate(this.form, res).then(() => {
          this.isLoading = false;
          this.form.enable();
          this.form.get('image').setValue(res.imageUrl);
        });
      }
    });
  }

  protected initForm() {
    this.form = this._fb.group({
      templateType: [null, [Validators.required]],
      subject: [null, [Validators.required]],
      templateBody: [null, [Validators.required]],
      headerContent: [null, [Validators.required]],
      footerContent: [null, [Validators.required]],
      url: [null, [Validators.required, Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')]],
      image: [null],
    });
  }
}
